import React, { useState } from "react";
import {
  TouchableOpacity,
  StyleSheet,
  TextInput,
  View,
  Modal,
  Text,
  Alert,
} from "react-native";
import { Badge, Button } from "react-native-paper";
import { renameLabel } from "../../../utils/common";
import { getAllSalesFilteredOperationsList } from "../../../services/operationService";
import { useSelector } from "react-redux";
import { useAppTheme } from "../../../hooks/useAppTheme";
import type { ThemeColors } from "../../../utils/constants/colors";

interface Props {
  itemsList: Array<string>;
  filterCategory: string;
  setFilterCategory: (category: string) => void;
  tableHeaders: any; // Add tableHeaders prop if it's not global
}

export const SalesOperationBadgeHorizontalList: React.FC<Props> = ({
  itemsList,
  filterCategory,
  setFilterCategory,
  tableHeaders,
}) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const userInfo = useSelector((state: any) => state.configurations.userInfo);

  const handleDateChange = (key: "from" | "to", value: string) => {
    setDateRange((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilter = (filter: string) => {
    setFilterCategory(filter);
  };

  const validateAndApply = () => {
    if (!dateRange.from || !dateRange.to) {
      setErrorMessage("Both 'From' and 'To' dates are required.");
      return;
    }

    handleCustomDateApply(dateRange);
    setIsModalVisible(false);
  };

  const handleCustomDateApply = (dateRange: any) => {
    if (dateRange.from && dateRange.to) {
      const customFilter = `From: ${dateRange.from} To: ${dateRange.to}`;
      applyFilter(customFilter);
    }
    setIsModalVisible(false);
    let filters: { status?: string; dateRange?: { from: string; to: string } } =
      {};
    filters = {
      dateRange: dateRange,
    };
    getAllSalesFilteredOperationsList(userInfo?.user_id, tableHeaders, filters);
  };

  const handleBadgeClick = (item: string) => {
    const today = new Date();
    let filters: { status?: string; dateRange?: { from: string; to: string } } =
      {};

    if (item === "Custom Date Range") {
      setIsModalVisible(true); // Open modal for custom date selection
      return;
    } else if (item === "Last 3 months") {
      const threeMonthsAgo = new Date(
        today.getFullYear(),
        today.getMonth() - 3,
        today.getDate()
      );
      setDateRange({
        from: threeMonthsAgo.toISOString().split("T")[0],
        to: today.toISOString().split("T")[0],
      });
      filters = {
        dateRange: {
          from: threeMonthsAgo.toISOString().split("T")[0],
          to: today.toISOString().split("T")[0],
        },
      };
    } else {
      filters = { status: item };
    }

    applyFilter(item); // Update the selected badge visually
    getAllSalesFilteredOperationsList(userInfo?.user_id, tableHeaders, filters); // Pass filters for API call
  };

  return (
    <View style={styles.badgesRow}>
      {/* Badge Row */}
      {itemsList.map((category, index) => (
        <TouchableOpacity
          key={index}
          style={[
            styles.filterButton,
            filterCategory === category && styles.activeFilterButton,
          ]}
          onPress={() => handleBadgeClick(category)}
        >
          <Text
            style={
              filterCategory === category
                ? styles.activeFilterText
                : styles.filterText
            }
          >
            {renameLabel(category)}
          </Text>
        </TouchableOpacity>
      ))}

      {/* Custom Date Range Badge */}
      <TouchableOpacity
        style={[
          styles.filterButton,
          filterCategory.includes("From") && styles.activeFilterButton,
        ]}
        onPress={() => handleBadgeClick("Custom Date Range")}
      >
        <Text
          style={[
            filterCategory.includes("From")
              ? styles.activeFilterText
              : styles.filterText,
          ]}
        >
          {filterCategory.includes("From")
            ? filterCategory
            : "Custom Date Range"}
        </Text>
      </TouchableOpacity>

      {/* Custom Date Range Modal */}
      <Modal
        visible={isModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setIsModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Custom Date Range</Text>

            {/* From Date Input */}
            <TextInput
              style={styles.dateInput}
              placeholder="Select From Date"
              value={dateRange.from}
              onChangeText={(value) => handleDateChange("from", value)}
              onFocus={(e) => ((e.currentTarget as any).type = "date")} // Show date picker on focus
            />

            {/* To Date Input */}
            <TextInput
              style={styles.dateInput}
              placeholder="Select To Date"
              value={dateRange.to}
              onChangeText={(value) => handleDateChange("to", value)}
              onFocus={(e) => ((e.currentTarget as any).type = "date")} // Show date picker on focus
            />

            {/* Error Message */}
            {errorMessage ? (
              <Text style={styles.errorMessage}>{errorMessage}</Text>
            ) : null}

            {/* Modal Buttons */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setIsModalVisible(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.applyButton}
                onPress={validateAndApply}
              >
                <Text style={styles.applyButtonText}>Apply</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      paddingHorizontal: 10,
    },
    badgesRow: {
      flexDirection: "row",
      flexWrap: "wrap",
    },
    badgeStyle: {
      marginHorizontal: 4,
      fontSize: 12,
      paddingHorizontal: 10,
    },
    inActiveBadge: {
      color: "black",
      backgroundColor: "rgba(0, 0, 0, 0.12)",
    },
    activeBadge: {
      color: "white",
      backgroundColor: "rgb(47, 149, 220)",
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      justifyContent: "center",
      alignItems: "center",
    },
    modalContent: {
      backgroundColor: colors.surface,
      width: "92%",
      maxWidth: 400,
      borderRadius: 12,
      padding: 24,
      alignItems: "center",
    },
    modalTitle: {
      fontSize: 19,
      fontWeight: "600",
      color: colors.text,
      marginBottom: 20,
      textAlign: "center",
    },
    dateInput: {
      width: "100%",
      backgroundColor: colors.background,
      borderRadius: 8,
      paddingHorizontal: 16,
      paddingVertical: 12,
      marginBottom: 16,
      fontSize: 15,
      color: colors.text,
    },
    modalActions: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
      marginTop: 8,
      gap: 12,
    },
    cancelButton: {
      flex: 1,
      backgroundColor: colors.surface,
      borderColor: colors.textMuted,
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: "center",
    },
    cancelButtonText: {
      color: "#374151",
      fontSize: 15,
      fontWeight: "600",
    },
    applyButton: {
      flex: 1,
      backgroundColor: "#2563EB",
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: "center",
    },
    applyButtonText: {
      color: "#FFFFFF",
      fontSize: 15,
      fontWeight: "600",
    },
    errorMessage: {
      color: "red",
      marginBottom: 10,
      textAlign: "center",
    },
    filterMainContainer: {
      width: "100%",
    },
    filterContainer: {
      flexDirection: "row",
      paddingVertical: 10,
      paddingHorizontal: 15,
    },
        filterButton: {
      paddingVertical: 6,
      paddingHorizontal: 14,
      backgroundColor: colors.surfaceSubtle,
      borderRadius: 999,
      marginRight: 8,
      marginBottom: 6,
    },
    activeFilterButton: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    filterText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontWeight: "500",
    },
    activeFilterText: {
      color: "#fff",
      fontSize: 13,
      fontWeight: "600",
    },
  });
}
